import { createRoute, useNavigate } from '@tanstack/react-router'
import { useContext, useEffect, useState } from 'react'
import {
  ArrowRight,
  Calendar,
  MapPin,
  Pencil,
  Plus,
  Settings,
  Tag,
} from 'lucide-react'
import rootRoute from './rootRoute'
import type { Event } from '@/lib/types'
import type { components } from '@/lib/client'
import {
  Avatar,
  Button,
  Card,
  FilterPill,
  Row,
  Stack,
  spacing,
} from '@/components/ui'
import StatusPill from '@/components/StatusPill'
import DateWarning from '@/components/DateWarning'
import { catOf } from '@/lib/types'
import { LoginContext, loggedInRouteLoader } from '@/lib/auth'
import { $api } from '@/lib/fetch'
import { EventStatus } from '@/lib/constants'
import { isLikelyInvalidDate, shortDate, splitAddress } from '@/lib/helpers'
import LoadMore from '@/components/LoadMore'
import Loader from '@/components/Loader'

type PaginationToken = components['schemas']['PaginationToken']

const TABS: Array<[string, string]> = [
  ['all', 'Tous'],
  [EventStatus.NotVerified, 'En attente'],
  [EventStatus.Verified, 'Vérifiés'],
  [EventStatus.SoftDelete, 'Supprimés'],
]

function EventRow({ event, isAdmin }: { event: Event; isAdmin: boolean }) {
  const navigate = useNavigate()
  const cat = catOf(event.type.type)
  const { venue, city } = splitAddress(event.location.address)
  // A validated event can no longer be edited by its (non-admin) owner.
  const canEdit = isAdmin || event.status === EventStatus.NotVerified
  return (
    <Card padding={0}>
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        <div style={{ width: 6, background: cat.color, flexShrink: 0 }} />
        <div
          style={{
            flex: 1,
            padding: '18px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 18,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, minWidth: 220 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 6,
                flexWrap: 'wrap',
              }}
            >
              <h3 style={{ fontSize: 18, margin: 0, color: 'var(--ink-0)' }}>
                {event.title}
              </h3>
              <StatusPill status={event.status} />
            </div>
            <div
              style={{
                display: 'flex',
                gap: 16,
                flexWrap: 'wrap',
                fontSize: 13.5,
                color: 'var(--ink-2)',
              }}
            >
              <Row gap="xs" style={{ display: 'inline-flex' }}>
                <Calendar size={14} />{' '}
                {isLikelyInvalidDate(event.start_date) ? (
                  <DateWarning compact />
                ) : (
                  shortDate(event.start_date)
                )}
              </Row>
              <Row gap="xs" style={{ display: 'inline-flex' }}>
                <MapPin size={14} /> {venue}
                {city && ` · ${city}`}
              </Row>
              <Row gap="xs" style={{ display: 'inline-flex' }}>
                <Tag size={14} /> {event.type.type}
              </Row>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <span
              title={
                canEdit
                  ? undefined
                  : 'Un événement validé ne peut plus être modifié'
              }
              style={{ display: 'inline-flex' }}
            >
              <Button
                variant="ghost"
                size="sm"
                iconLeft={<Pencil size={15} />}
                disabled={!canEdit}
                style={{
                  border: '1px solid var(--border-soft)',
                  ...(canEdit
                    ? null
                    : { opacity: 0.45, cursor: 'not-allowed' }),
                }}
                onClick={() =>
                  navigate({
                    to: '/events/$id/edit',
                    params: { id: String(event.id) },
                  })
                }
              >
                Modifier
              </Button>
            </span>
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<ArrowRight size={15} />}
              style={{ border: '1px solid var(--border-soft)' }}
              onClick={() =>
                navigate({
                  to: '/events/$id',
                  params: { id: String(event.id) },
                })
              }
            >
              Voir
            </Button>
          </div>
        </div>
      </div>
    </Card>
  )
}

export function MyEventsPage() {
  const ctx = useContext(LoginContext)
  const isAdmin = ctx?.user?.admin ?? false
  const navigate = useNavigate()
  const [filter, setFilter] = useState<string>('all')
  const [pageToken, setPageToken] = useState<PaginationToken | undefined>()
  const [accumulated, setAccumulated] = useState<Array<Event>>([])

  const { data, isLoading, isFetching, error } = $api.useQuery(
    'get',
    '/api/events/me',
    {
      params: {
        query: {
          limit: 20,
          last_id: pageToken?.last_id,
          last_ts: pageToken?.last_start_timestamp_ms,
        },
      },
    },
  )

  useEffect(() => {
    if (!data?.events) return
    setAccumulated((prev) =>
      pageToken == null
        ? (data.events ?? [])
        : [...prev, ...(data.events ?? [])],
    )
  }, [data])

  if (isLoading && accumulated.length === 0) return <Loader />
  if (error && accumulated.length === 0) {
    return (
      <div style={{ padding: 'var(--space-20)', textAlign: 'center' }}>
        Erreur: {JSON.stringify(error)}
      </div>
    )
  }

  const mine = accumulated
  const counts: Record<string, number> = {
    all: mine.length,
    [EventStatus.Verified]: mine.filter(
      (e) => e.status === EventStatus.Verified,
    ).length,
    [EventStatus.NotVerified]: mine.filter(
      (e) => e.status === EventStatus.NotVerified,
    ).length,
    [EventStatus.SoftDelete]: mine.filter(
      (e) => e.status === EventStatus.SoftDelete,
    ).length,
  }
  const list = filter === 'all' ? mine : mine.filter((e) => e.status === filter)

  return (
    <div
      style={{
        maxWidth: 'var(--container-max)',
        margin: '0 auto',
        padding: 'var(--space-8) var(--gutter) var(--space-20)',
      }}
    >
      <Row gap="lg" wrap style={{ marginBottom: spacing.xl }}>
        <Avatar name={ctx?.user?.email ?? '?'} tone="indigo" size={64} ring />
        <div style={{ flex: 1, minWidth: 200 }}>
          <h1 style={{ fontSize: 'var(--text-h2)', margin: '0 0 4px' }}>
            Mon compte
          </h1>
          <div style={{ fontSize: 15, color: 'var(--ink-2)' }}>
            {ctx?.user?.email}
          </div>
        </div>
        <Row gap="sm" wrap>
          <Button
            variant="secondary"
            iconLeft={<Settings size={17} />}
            onClick={() => navigate({ to: '/me' })}
          >
            Paramètres
          </Button>
          <Button
            variant="gold"
            iconLeft={<Plus size={17} />}
            onClick={() => navigate({ to: '/new-event' })}
          >
            Proposer
          </Button>
        </Row>
      </Row>

      <div className="oumma-overline" style={{ marginBottom: 14 }}>
        Mes événements proposés
      </div>

      <Row wrap gap="sm" style={{ marginBottom: spacing.md }}>
        {TABS.map(([k, lbl]) => {
          const on = filter === k
          return (
            <FilterPill key={k} active={on} onClick={() => setFilter(k)}>
              {lbl}{' '}
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  opacity: 0.8,
                }}
              >
                {counts[k]}
              </span>
            </FilterPill>
          )
        })}
      </Row>

      {list.length ? (
        <>
          <Stack gap="md">
            {list.map((e) => (
              <EventRow key={e.id} event={e} isAdmin={isAdmin} />
            ))}
          </Stack>
          <LoadMore
            onLoadMore={() => setPageToken(data?.next_page_token)}
            loading={isFetching}
            hasMore={!!data?.next_page_token}
          />
        </>
      ) : (
        <Card padding={40}>
          <div style={{ textAlign: 'center', color: 'var(--ink-3)' }}>
            <img
              src="/assets/oumma-mark.svg"
              alt=""
              width="40"
              height="40"
              style={{ opacity: 0.4, marginBottom: 12 }}
            />
            <p
              style={{
                fontSize: 16,
                color: 'var(--ink-2)',
                margin: '0 0 18px',
              }}
            >
              Aucun événement{' '}
              {filter !== 'all' ? 'dans cette catégorie' : "pour l'instant"} —
              lancez votre premier événement.
            </p>
            <Button
              variant="primary"
              iconLeft={<Plus size={17} />}
              onClick={() => navigate({ to: '/new-event' })}
            >
              Proposer un événement
            </Button>
          </div>
        </Card>
      )}
    </div>
  )
}

export const myEventsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/my-events',
  loader: loggedInRouteLoader as any,
}).lazy(() => import('./MyEvents.lazy').then((m) => m.Route))

export default MyEventsPage
